import React, { useState, useRef } from "react";
import authService from "../Auth/AuthService";
import excelFile from '../assets/TeacherTemplate.xlsx'
import {
  Box, Card, Typography, Button, Stack, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, Skeleton
} from "@mui/material";

import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DescriptionIcon from "@mui/icons-material/Description";

export default function TeacherExcelUpload() {
  const [file, setFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewErrors, setPreviewErrors] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const inputRef = useRef(null);
  const [show, setShow] = useState(true);

  function humanFileSize(size) {
    if (size === 0) return "0 B";
    const i = Math.floor(Math.log(size) / Math.log(1024));
    return (size / Math.pow(1024, i)).toFixed(1) + " " + ["B", "KB", "MB", "GB"][i];
  }

  const onFileChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    setMessage(null);
    setPreviewRows([]);
    setPreviewErrors([]);
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0] ?? null;
    if (f) {
      setFile(f);
      setMessage(null);
      setPreviewRows([]);
      setPreviewErrors([]);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const handlePreview = async () => {
    if (!file) {
      setMessage({ type: "error", text: "Please select an Excel file first." });
      return;
    }
    setShow(false)
    const form = new FormData();
    form.append("file", file);
    console.log(form);

    try {
      setLoadingPreview(true);
      setMessage(null);
      const url = authService.pathUrl("POST", "/teacher/excel/upload/preview")
      const res = await authService.restPostFormData(url, form);

      const data = res;
      setPreviewRows(data.students ?? []);
      setPreviewErrors(data.errors ?? []);
      setMessage({ type: "success", text: `Preview parsed ${data.validCount ?? (data.students ?? []).length} rows` });
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message ?? err.message ?? "Preview failed";
      setMessage({ type: "error", text: msg });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSave = async () => {
    if (!file) {
      setMessage({ type: "error", text: "Please select an Excel file first." });
      return;
    }

    const form = new FormData();
    form.append("file", file);

    try {
      setSaving(true);
      setMessage(null);
      const url = authService.pathUrl("POST", "/teacher/excel/upload")
      const res = await authService.restPostFormData(url, form);

      const data = res.data;
      setMessage({ type: "success", text: `Saved ${data?.savedCount ?? 0} employee` });
      setPreviewRows(data.students ?? []);
      setPreviewErrors([]);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message ?? err.message ?? "Save failed";
      const payload = err?.response?.data ?? null;
      if (payload?.errors) setPreviewErrors(payload.errors);
      setMessage({ type: "error", text: msg });
    } finally {
      setSaving(false);
    }
  };

  const clear = () => {
    setFile(null);
    setShow(true)
    setPreviewRows([]);
    setPreviewErrors([]);
    setMessage(null);
    if (inputRef.current) inputRef.current.value = null;
  };

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", py: 5 }}>
      {show && (<>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Upload Teachers Excel
        </Typography>

        {/* Excel Upload Card */}
        <Card
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            border: "1px solid #e2e8f0",
            background: "linear-gradient(to right, #f8fafc, #ffffff)",
          }}
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          <Box textAlign="center">

            {/* Excel Icon Preview */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              {file ? (
                <DescriptionIcon sx={{ fontSize: 60, color: "#1e40af" }} />
              ) : (
                <InsertDriveFileIcon sx={{ fontSize: 60, color: "#94a3b8" }} />
              )}
            </Box>

            <Typography variant="body1" color="text.secondary">
              Drag & drop your Excel file here
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              or click to browse files
            </Typography>

            <Button variant="contained" sx={{
              mt: 3,
              cursor: "pointer"
            }}
              onClick={() => inputRef.current?.click()}>
              Choose File
            </Button>

            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={onFileChange}
              style={{ display: "none" }}
            />

            {/* File Information */}
            <Box mt={3}>
              {file ? (
                <Typography variant="body2" color="text.secondary">
                  <strong>{file.name}</strong> — {humanFileSize(file.size)}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No file selected
                </Typography>
              )}
            </Box>

            {/* Template Download Link */}
            <Box mt={2}>
              <a href={excelFile} underline="hover" color="primary" target="_blank">
                Download Template
              </a>
            </Box>
          </Box>
        </Card>
      </>)}
      {/* Buttons */}
      <Stack direction="row" spacing={2} mb={3}>
        <Button
          variant="contained"
          color="success"
          disabled={!file || loadingPreview}
          onClick={handlePreview}
        >
          {loadingPreview ? "Previewing..." : "Preview"}
        </Button>

        <Button
          variant="contained"
          color="primary"
          disabled={!file || saving}
          onClick={handleSave}
        >
          {saving ? "Saving..." : "Save to DB"}
        </Button>

        <Button variant="outlined" onClick={clear}>
          Clear
        </Button>
      </Stack>

      {/* Progress Bar - Shows During Preview or Save */}
      {(loadingPreview || saving) && (
        <Box sx={{ width: "100%", mb: 3 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Alerts */}
      {message && (
        <Alert
          severity={message.type === "error" ? "error" : "success"}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {message.text}
        </Alert>
      )}

      {/* Validation Errors */}
      {previewErrors?.length > 0 && (
        <Card sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Validation Errors
          </Typography>

          <Box sx={{ maxHeight: 250, overflowY: "auto" }}>
            {previewErrors.map((err, idx) => (
              <Card
                key={idx}
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: "#fff5f5",
                  border: "1px solid #fecaca",
                  borderRadius: 2
                }}
              >
                <Typography fontWeight={600} color="error.main">
                  Row {err.rowNumber}
                </Typography>
                <ul style={{ marginLeft: "20px" }}>
                  {err.errors.map((e, i) => (
                    <li key={i} style={{ color: "#991b1b" }}>{e}</li>
                  ))}
                </ul>
              </Card>
            ))}
          </Box>
        </Card>
      )}

      {/* ------------------------------------------------------ */}
      {/* Skeleton Loader for Table Preview */}
      {/* ------------------------------------------------------ */}
      {loadingPreview && (
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Loading preview...
          </Typography>

          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={40}
              sx={{ mb: 2, borderRadius: 1 }}
            />
          ))}
        </Card>
      )}

      {/* ------------------------------------------------------ */}
      {/* Preview Table */}
      {/* ------------------------------------------------------ */}
      {previewRows?.length > 0 && !loadingPreview && (
        <Card sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom>
            Preview ({previewRows.length} rows)
          </Typography>

          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {Object.keys(previewRows[0]).map((h) => (
                    <TableCell
                      key={h}
                      sx={{ fontWeight: 600, bgcolor: "#f1f5f9" }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {previewRows.map((row, i) => (
                  <TableRow key={i} hover>
                    {Object.values(row).map((val, j) => (
                      <TableCell key={j}>
                        {typeof val === "object" && val !== null
                          ? val.addressLine1 + val.city || ['Ac:' + val.acctnumber, ' Br:' + val.branch, 'IFsc:' + val.ifsccode].join(", ")
                          : val}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );



}
